import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Clock3, Globe2, Shield, Building2 } from "lucide-react";
import Seo from "../components/Seo";
import { isPrerendered } from "../lib/hydration";
import { BUSINESS } from "../lib/businessInfo";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1545389336-cf090694435e?auto=format&fit=crop&q=80&w=2000";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const benefits = [
  {
    icon: Building2,
    title: "Built for schools & retreats",
    description:
      "A separate partner flow for yoga schools and retreat providers in India — not the individual instructor path.",
  },
  {
    icon: Globe2,
    title: "You bring the website",
    description:
      "Share org details and your public URL. Our developers list programs from your site the same way existing scrapers work.",
  },
  {
    icon: Shield,
    title: "Reviewed before go-live",
    description:
      "Submit queues setup for our team. Approval is the final gate after listings look correct — never an auto-scrape trigger.",
  },
  {
    icon: Clock3,
    title: "Clear status while we work",
    description:
      "After submit you’ll see that our team is setting up programs from your website until scrape is ready and admin approves.",
  },
];

const steps = [
  {
    title: "Create a partner account",
    detail: "Sign up with ?as=partner, verify your email, then open the partner onboarding wizard.",
  },
  {
    title: "Share org details & website",
    detail:
      "Add legal/trade name, location, logo, policies, agreement, and your program website. No course catalogs or prices from you.",
  },
  {
    title: "Submit — we queue setup",
    detail:
      "Status becomes Submitted and scrape is queued. Developers manually write/run a scraper under scraper/ and load into Neon.",
  },
  {
    title: "Go live after review",
    detail:
      "When listings look correct, scrape is marked ready. Only then can admin Approve — that is the final gate.",
  },
];

const faqs = [
  {
    q: "Is this the same as becoming an instructor?",
    a: "No. Instructor onboarding is for individual teachers. Partner onboarding is a separate flow for yoga schools and retreat providers.",
  },
  {
    q: "Do I upload my course calendar?",
    a: "No. You provide details and a website URL. Our developers list programs from your site offline.",
  },
  {
    q: "Does submitting start an automatic scraper?",
    a: "No. Submit only queues work for our team. Scrapers are written and run manually by developers, like existing folders under scraper/.",
  },
  {
    q: "When do I go live?",
    a: "After developers mark scrape ready and an admin approves. Approve is blocked until at least one source is ready.",
  },
];

const TITLE = "Become a Yoga School / Retreat Partner";
const DESCRIPTION =
  "Partner with Yoga Retreats Booking as a yoga school or retreat provider in India. Share your website — our team lists programs after review.";

export default function BecomePartnerPage() {
  // #region agent log
  React.useEffect(() => {
    const signup = document.querySelector('a[href="/signup?as=partner"]');
    const login = document.querySelector('a[href="/login?as=partner"]');
    const sample = (el) => {
      if (!el) return null;
      const r = el.getBoundingClientRect();
      const cx = Math.round(r.left + r.width / 2);
      const cy = Math.round(r.top + r.height / 2);
      const top = document.elementFromPoint(cx, cy);
      return {
        href: el.getAttribute("href"),
        rect: { x: r.x, y: r.y, w: r.width, h: r.height },
        center: { cx, cy },
        topTag: top?.tagName || null,
        topHref: top?.closest?.("a")?.getAttribute?.("href") || null,
        blocked: Boolean(top && el !== top && !el.contains(top)),
      };
    };
    fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'pre-fix',hypothesisId:'A',location:'BecomePartnerPage.jsx:mount',message:'Partner landing mount + CTA hit-test',data:{path:window.location.pathname,signup:sample(signup),login:sample(login)},timestamp:Date.now()})}).catch(()=>{});
  }, []);
  // #endregion

  return (
    <div
      className="min-h-screen pt-16"
      style={{
        "--bi-forest": "#0c3b2e",
        "--bi-moss": "#1a5c45",
        "--bi-leaf": "#2f8f6b",
        "--bi-mist": "#e8f3ee",
        "--bi-honey": "#e8c47c",
        "--bi-honey-deep": "#c9a24a",
        "--bi-ink": "#12261f",
        "--bi-muted": "#4d655c",
        background:
          "linear-gradient(180deg, #f4faf7 0%, #ffffff 35%, #f7fbf9 70%, #eef7f2 100%)",
        fontFamily: "'Outfit', 'Inter', sans-serif",
      }}
    >
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,650;9..144,700&family=Outfit:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <Seo
        title={TITLE}
        description={DESCRIPTION}
        canonicalPath="/become-a-partner"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: TITLE,
          url: "https://yogaretreatsbooking.com/become-a-partner",
          description: DESCRIPTION,
        }}
      />

      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--bi-forest)]/92 via-[var(--bi-moss)]/78 to-[var(--bi-forest)]/55" />
        <div className="relative mx-auto flex min-h-[78vh] max-w-6xl flex-col justify-end px-4 pb-16 pt-28 sm:px-6 lg:px-8">
          <motion.p
            initial={isPrerendered ? false : "hidden"}
            animate="visible"
            variants={fadeUp}
            custom={0}
            className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-[var(--bi-honey)]"
          >
            {BUSINESS.tradeName}
          </motion.p>
          <motion.h1
            initial={isPrerendered ? false : "hidden"}
            animate="visible"
            variants={fadeUp}
            custom={1}
            className="max-w-3xl font-semibold text-white"
            style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "clamp(2.4rem, 5vw, 3.6rem)" }}
          >
            Become a partner
          </motion.h1>
          <motion.p
            initial={isPrerendered ? false : "hidden"}
            animate="visible"
            variants={fadeUp}
            custom={2}
            className="mt-4 max-w-xl text-base leading-relaxed text-white/90 sm:text-lg"
          >
            For yoga schools and retreat providers in India. You provide details and your website; our
            developers list programs from your site; you go live after review.
          </motion.p>
          <motion.div
            initial={isPrerendered ? false : "hidden"}
            animate="visible"
            variants={fadeUp}
            custom={3}
            className="mt-8 flex flex-wrap gap-3"
          >
            <Link
              to="/signup?as=partner"
              onClick={() => {
                // #region agent log
                fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'pre-fix',hypothesisId:'A',location:'BecomePartnerPage.jsx:signup-cta',message:'Partner signup CTA clicked',data:{href:'/signup?as=partner',path:window.location.pathname},timestamp:Date.now()})}).catch(()=>{});
                // #endregion
              }}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--bi-honey)] px-6 py-3 text-sm font-semibold text-[var(--bi-ink)] shadow-lg hover:bg-[var(--bi-honey-deep)]"
            >
              Start partner signup <ArrowRight size={16} />
            </Link>
            <Link
              to="/login?as=partner"
              onClick={() => {
                // #region agent log
                fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'pre-fix',hypothesisId:'A',location:'BecomePartnerPage.jsx:login-cta',message:'Partner sign-in CTA clicked',data:{href:'/login?as=partner',path:window.location.pathname},timestamp:Date.now()})}).catch(()=>{});
                // #endregion
              }}
              className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-6 py-3 text-sm font-semibold text-white backdrop-blur hover:bg-white/20"
            >
              Partner sign in
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <h2
          className="text-center text-3xl font-semibold text-[var(--bi-ink)]"
          style={{ fontFamily: "'Fraunces', Georgia, serif" }}
        >
          Why partner with us
        </h2>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {benefits.map((b, i) => (
            <motion.div
              key={b.title}
              initial={isPrerendered ? false : "hidden"}
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              className="rounded-2xl border border-emerald-100 bg-white/90 p-6 shadow-sm"
            >
              <b.icon className="text-emerald-700" size={22} />
              <h3 className="mt-3 text-lg font-semibold text-gray-900">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">{b.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="how-it-works" className="bg-[var(--bi-mist)]/60 py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2
            className="text-center text-3xl font-semibold text-[var(--bi-ink)]"
            style={{ fontFamily: "'Fraunces', Georgia, serif" }}
          >
            How partner onboarding works
          </h2>
          <ol className="mt-10 space-y-6">
            {steps.map((step, index) => (
              <li key={step.title} className="flex gap-4 rounded-2xl border border-emerald-100 bg-white p-5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-semibold text-white">
                  {index + 1}
                </span>
                <div>
                  <h3 className="font-semibold text-gray-900">{step.title}</h3>
                  <p className="mt-1 text-sm text-gray-600">{step.detail}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-8 flex justify-center">
            <Link
              to="/signup?as=partner"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              Create partner account <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <h2
          className="text-center text-3xl font-semibold text-[var(--bi-ink)]"
          style={{ fontFamily: "'Fraunces', Georgia, serif" }}
        >
          FAQ
        </h2>
        <div className="mt-8 space-y-4">
          {faqs.map((faq) => (
            <details
              key={faq.q}
              className="group rounded-2xl border border-emerald-100 bg-white p-5 open:shadow-sm"
            >
              <summary className="cursor-pointer list-none font-semibold text-gray-900">
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  {faq.q}
                </span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-gray-600">{faq.a}</p>
            </details>
          ))}
        </div>
        <p className="mt-10 text-center text-sm text-gray-600">
          Individual teachers?{" "}
          <Link to="/become-an-instructor" className="font-medium text-emerald-700 underline">
            Become an instructor
          </Link>
          {" · "}
          <Link to="/partnership-agreement" className="font-medium text-emerald-700 underline">
            Partnership agreement
          </Link>
        </p>
      </section>
    </div>
  );
}
